
import { computed, defineComponent, nextTick, onMounted, onUnmounted, PropType, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import {
  BusinessScene,
  SoftFormat,
  SoftGroup,
  Software,
  SoftwareSelectService
} from '@/services/software-select.service'

export default defineComponent({
  components: { EmptyData },
  props: {
    allAble: {
      type: Boolean,
      default: true
    },
    modelValue: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    popper: {
      type: Boolean,
      default: true
    },
    /** 仅在popper = true时生效 */
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    placement: {
      type: String as PropType<'top'|'bottom'>,
    },
    /** 1：软件禁用，2：水印 */
    businessScene: {
      type: Number as PropType<1|2>,
      default: BusinessScene.softwareDisable
    }
  },

  setup (props, context) {
    const { t, locale } = useI18n()
    const displayPlaceholder = computed(() => props.placeholder || t('common.phSoftwareName'))
    const selectAllLabel = computed(() => t('common.selectAll'))
    const injector = useReflectiveInjector([SoftwareSelectService])
    const service = injector.get(SoftwareSelectService)

    const rootNode = ref<Node & HTMLElement>()
    const selectedNode = ref<Node & HTMLElement>()
    const isLoading = ref(false)
    const softwareGroupList = ref<SoftGroup[]>([]) // 软件分组列表(不含软件列表)
    const allSoftwareList = ref<Software[]>([]) // 所有软件列表
    const checkIds = ref<number[]>(props.modelValue)
    const activeGroupIndex = ref(0)
    const popperShow = ref(false)

    const searchParams = reactive({
      searchKey: '',
    })

    // 展示的软件树
    const softwareShowTree = computed<SoftFormat[]>(() => {
      const result: SoftFormat[] = softwareGroupList.value.map(v => ({ ...v, software_list: [] }))
      const filterSoftware = allSoftwareList.value.filter(v => charMatch(v.soft_name, searchParams.searchKey))
      filterSoftware.forEach(software => {
        const groupIndex = result.findIndex(v => v.group_id === software.soft_group_id)
        result[groupIndex]?.software_list?.push(software)
      })
      return result.filter(v => v.software_list.length)
    })

    // 展示的软件树的扁平化软件id列表
    const softwareShowIds = computed(() => {
      return softwareShowTree.value
        .reduce((acc, v) => acc.concat(v.software_list), [])
        .map(v => v.soft_id)
    })

    const allIndeterminate = computed(() => {
      const indeterminate = softwareShowIds.value.findIndex(v => !checkIds.value.includes(v)) > -1
      return checkIds.value.length && indeterminate
    })

    function charMatch (value: string, chars: string) {
      return value.toLowerCase().includes(chars.trim().toLowerCase())
    }

    function updateSoftGroup () {
      service.getSoftGroup({
        business_scene: props.businessScene
      }).then(response => {
        softwareGroupList.value = response.list
      })
    }

    function updateSoftwareList () {
      isLoading.value = true
      service.getAllSoftware(props.businessScene).then(response => {
        allSoftwareList.value = response
        nextTick(() => selectedNodeScrollBottom())
      }).finally(() => {
        isLoading.value = false
      })
    }

    function getGroupChecked (item: SoftFormat) {
      const checked = item.software_list.filter(v => checkIds.value.find(j => j === v.soft_id))
      return checked.length
    }

    function getSoftwareIsChecked (item: Software) {
      return !!checkIds.value.find(v => v === item.soft_id)
    }

    function checkSoftware (e, item: Software) {
      if (e) {
        checkIds.value.push(item.soft_id)
      } else {
        const index = checkIds.value.findIndex(v => v === item.soft_id)
        checkIds.value.splice(index, 1)
      }
    }

    function checkGroup (e, item: SoftFormat) {
      const check = e || (getGroupChecked(item) && getGroupChecked(item) !== item.software_list.length)
      item.software_list.forEach(soft => {
        const index = checkIds.value.findIndex(v => v === soft.soft_id)
        if (check && index === -1) {
          checkIds.value.push(soft.soft_id)
        } else if (!check) {
          checkIds.value.splice(index, 1)
        }
      })
    }

    function checkAll () {
      if (!checkIds.value.length || allIndeterminate.value) {
        checkIds.value = [ ...softwareShowIds.value ]
      } else {
        checkIds.value = []
      }
    }

    function selectedNodeScrollBottom () {
      nextTick(() => selectedNode.value && (selectedNode.value.scrollTop = 99999))
    }

    function onDocumentPointerClose (e: MouseEvent) {
      if (!props.popper || !popperShow.value) return
      const root = rootNode.value
      const target = e.target as Node | null
      if (!root || !target || root.contains(target)) return
      popperShow.value = false
    }

    updateSoftGroup()
    updateSoftwareList()

    watch(() => locale.value, () => {
      updateSoftGroup()
    })

    watch(() => props.modelValue, (newVal, oldValue) => {
      if (JSON.stringify(newVal) !== JSON.stringify(oldValue)) {
        checkIds.value = props.modelValue
      }
    })

    watch(checkIds, (value) => {
      selectedNodeScrollBottom()
      context.emit('update:modelValue', value)
      context.emit('update', value, value.map(v => allSoftwareList.value.find(j => j.soft_id === v)))
    }, { deep: true, immediate: true })

    watch(popperShow, (value) => {
      if (!props.popper) return
      if (value) {
        (selectedNode.value?.querySelector('.embed-search') as any)?.focus()
        selectedNodeScrollBottom()
      } else {
        searchParams.searchKey = ''
      }
    })

    watch(softwareShowTree, () => {
      activeGroupIndex.value = 0
    })

    onMounted(() => {
      document.addEventListener('mousedown', onDocumentPointerClose, true)
    })

    onUnmounted(() => {
      document.removeEventListener('mousedown', onDocumentPointerClose, true)
    })

    return {
      displayPlaceholder,
      selectAllLabel,
      rootNode,
      selectedNode,
      isLoading,
      popperShow,
      allSoftwareList,
      softwareShowTree,
      searchParams,
      allIndeterminate,
      checkIds,
      activeGroupIndex,
      getGroupChecked,
      getSoftwareIsChecked,
      checkGroup,
      checkSoftware,
      checkAll,
    }
  }
})
